import { useState, useEffect, useRef } from "react";
import { useAuth, useUser, useClerk } from "@clerk/clerk-react";
import Dashboard from "./Dashboard";
import "./LeadResearch.css";

const BACKEND_URL = 'https://api.rakaba.io';

const AGENT_STEPS = [
  { id: 'research', label: 'Researcher', icon: '🔍', desc: 'Scanning company data...' },
  { id: 'score',    label: 'Scorer',     icon: '📊', desc: 'Evaluating lead quality...' },
  { id: 'email',    label: 'Writer',     icon: '✍️',  desc: 'Drafting outreach email...' },
  { id: 'log',      label: 'Logger',     icon: '💾', desc: 'Saving to Google Sheets...' },
];

function RakabaLogo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
      <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
      <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
      <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
      <line x1="18" y1="21.5" x2="18" y2="30" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

async function runLeadPipeline(company, onStepUpdate, onComplete, token) {
  try {
    onStepUpdate('research', 'running');
    const res = await fetch(`${BACKEND_URL}/api/leads/research`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': process.env.REACT_APP_API_KEY, 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ company })
    });
    onStepUpdate('research', 'done');
    onStepUpdate('score', 'running');
    const data = await res.json();
    if (res.status === 429 && data.error === 'limit_reached') {
      onComplete(null, { type: 'limit_reached', message: data.message, plan: data.plan });
      return;
    }
    if (!data.success) throw new Error(data.error);
    onStepUpdate('score', 'done');
    onStepUpdate('email', 'running');
    onStepUpdate('email', 'done');
    onStepUpdate('log', 'running');
    onStepUpdate('log', 'done');
    const report = data.report;
    // history refreshed from Supabase after research completes
    onComplete(report, null);
  } catch (err) {
    onComplete(null, err.message);
  }
}

function parseScore(s)       { const m = s?.match(/SCORE:\s*(\d+)/);           return m ? parseInt(m[1]) : 0; }
function parseTier(s)        { const m = s?.match(/TIER:\s*(\w+)/);            return m ? m[1] : 'UNKNOWN'; }
function parseBudget(s)      { const m = s?.match(/BUDGET_ESTIMATE:\s*(.+)/);  return m ? m[1].trim() : 'N/A'; }
function parseTiming(s)      { const m = s?.match(/TIMING:\s*(.+)/);           return m ? m[1].trim() : 'N/A'; }
function parseOpportunity(s) { const m = s?.match(/OPPORTUNITY:\s*(.+)/);      return m ? m[1].trim() : ''; }
function parsePainPoints(s)  { const m = s?.match(/- (.+)/g);                  return m ? m.map(x => x.replace('- ','').trim()).slice(0,3) : []; }

const TIER_CONFIG = {
  HOT:     { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   glow: '0 0 20px rgba(239,68,68,0.2)',   label: '🔥 HOT' },
  WARM:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  glow: '0 0 20px rgba(245,158,11,0.2)',  label: '⚡ WARM' },
  COLD:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  glow: '0 0 20px rgba(59,130,246,0.2)',  label: '❄️ COLD' },
  UNKNOWN: { color: '#484f58', bg: 'rgba(72,79,88,0.1)',    glow: 'none',                           label: '? N/A' },
};

export default function LeadResearch() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { user } = useUser();
  const [company, setCompany]       = useState('');
  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [history, setHistory]       = useState([]);
  const [, setHistoryLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSenderSetup, setShowSenderSetup] = useState(false);
  const [senderProfile, setSenderProfile] = useState(null);
  const [senderForm, setSenderForm] = useState({
    sender_product: '',
    sender_icp: '',
    sender_value_prop: '',
    sender_tone: 'direct',
    sender_role: ''
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeEmailVariant, setActiveEmailVariant] = useState(0);
  const [notes, setNotes] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [onboardStep, setOnboardStep] = useState(0);
  const [usage, setUsage] = useState(null);
  const [activeTab, setActiveTab]   = useState('research');
  const [stepStatus, setStepStatus] = useState({});
  const [toast, setToast]           = useState(null);
  const [favicon, setFavicon]       = useState(null);
  const [pageTab, setPageTab]       = useState('research');
  const inputRef                    = useRef(null);


  // ── FETCH HISTORY FROM SUPABASE ──────────────────────────────────────
  async function fetchHistory() {
    try {
      setHistoryLoading(true);
      setFetchError(null);
      const token = await getToken();
      if (!token) { setHistoryLoading(false); return; }
      const res = await fetch(`${BACKEND_URL}/api/leads/history`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        // Map Supabase format to match existing UI expectations
        const mapped = data.leads.map(l => ({
          id:        l.id,
          company:   l.company_name,
          timestamp: l.created_at,
          score:     `SCORE: ${l.score}\nTIER: ${l.tier}\nBUDGET_ESTIMATE: ${l.budget || 'N/A'}`,
          tier:      l.tier,
          budget:    l.budget,
          notes:     l.notes || ''
        }));
        // Load notes into state
        const notesMap = {};
        data.leads.forEach(l => { notesMap[l.id] = l.notes || ''; });
        setNotes(notesMap);
        setHistory(mapped);
      }
    } catch (err) {
      console.error('[fetchHistory]', err.message);
      setFetchError('Failed to load lead history. Check your connection.');
    } finally {
      setHistoryLoading(false);
    }
  }

  // Load history on mount
  useEffect(() => { 
    fetchHistory(); 
    fetchUsage();
    fetchSenderProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show onboarding for first-time users
  useEffect(() => {
    const seen = localStorage.getItem('rakaba-onboarded');
    if (!seen) setShowOnboarding(true);
  }, []);

  function completeOnboarding() {
    localStorage.setItem('rakaba-onboarded', '1');
    setShowOnboarding(false);
  }

  function onboardNext() {
    if (onboardStep < 2) setOnboardStep(s => s + 1);
    else completeOnboarding();
  }

  async function fetchUsage() {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/api/usage`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.plan) setUsage(data);
    } catch (err) {
      console.error('[fetchUsage]', err.message);
    }
  }

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Handle Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      showToast(`🎉 Welcome to ${params.get('plan')} plan!`);
      window.history.replaceState({}, '', '/leads');
    }
    if (params.get('upgrade') === 'cancelled') {
      showToast('Upgrade cancelled', 'error');
      window.history.replaceState({}, '', '/leads');
    }
  }, []);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); } }, [toast]);

  function showToast(msg, type = 'success') { setToast({ msg, type }); }
  function updateStep(stepId, status) { setStepStatus(prev => ({ ...prev, [stepId]: status })); }
  function fetchFavicon(name) { setFavicon(`https://www.google.com/s2/favicons?domain=${name.toLowerCase()}.com&sz=64`); }


  // ── STRIPE UPGRADE ───────────────────────────────────────────────────
  async function handleUpgrade(plan) {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan,
          email: user?.primaryEmailAddress?.emailAddress,
          name: user?.fullName
        })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else showToast('Failed to start checkout', 'error');
    } catch (err) {
      showToast('Checkout error: ' + err.message, 'error');
    }
  }

  async function handleBillingPortal() {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else showToast('Failed to open billing portal', 'error');
    } catch (err) {
      showToast('Portal error: ' + err.message, 'error');
    }
  }

  async function fetchFullLead(leadId) {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/leads/history/${leadId}`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.lead) {
        const l = data.lead;
        // Map Supabase full record to report format the UI expects
        return {
          id:        l.id,
          company:   l.company_name,
          timestamp: l.created_at,
          research:  l.summary,
          news:      l.news,
          tech:      l.tech_stack,
          funding:   l.funding,
          score:     `SCORE: ${l.score}\nTIER: ${l.tier}\nBUDGET_ESTIMATE: ${l.budget || 'N/A'}\nTIMING: ${l.timing || 'N/A'}\nOPPORTUNITY: ${l.opportunity || 'N/A'}\nPAIN_POINTS:\n${(l.pain_points||[]).map(p => `- ${p}`).join('\n')}`,
          email:     l.email_draft,
          tier:      l.tier,
          budget:    l.budget
        };
      }
    } catch (err) {
      console.error('[fetchFullLead]', err.message);
    }
    return null;
  }

  async function fetchSenderProfile() {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/api/profile/sender`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.profile) {
        setSenderProfile(data.profile);
        setSenderForm({
          sender_product:    data.profile.sender_product    || '',
          sender_icp:        data.profile.sender_icp        || '',
          sender_value_prop: data.profile.sender_value_prop || '',
          sender_tone:       data.profile.sender_tone       || 'direct',
          sender_role:       data.profile.sender_role       || ''
        });
      }
    } catch (err) {
      console.error('[fetchSenderProfile]', err.message);
    }
  }

  async function saveSenderProfile() {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/profile/sender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(senderForm)
      });
      const data = await res.json();
      if (data.success) {
        setSenderProfile(senderForm);
        setShowSenderSetup(false);
        showToast('Sender profile saved — emails will now be personalized to your product');
      }
    } catch (err) {
      showToast('Failed to save profile', 'error');
    }
  }

  async function saveNote(leadId, noteText) {
    try {
      setSavingNote(leadId);
      const token = await getToken();
      await fetch(`${BACKEND_URL}/api/leads/history/${leadId}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notes: noteText })
      });
      setNotes(prev => ({ ...prev, [leadId]: noteText }));
      showToast('Note saved');
    } catch (err) {
      showToast('Failed to save note', 'error');
    } finally {
      setSavingNote(null);
    }
  }

  async function handleResearch() {
    if (!company.trim() || loading) return;
    setLoading(true); setReport(null); setStepStatus({});
    setActiveTab('research'); setPageTab('research'); setFavicon(null);
    fetchFavicon(company.trim());
    const token = await getToken();
    await runLeadPipeline(company.trim(), updateStep, (result, err) => {
      if (err && err.type === 'limit_reached') { setShowUpgrade(true); showToast(err.message, 'error'); }
      else if (err) { showToast(`${err}`, 'error'); }
      else { setReport(result); fetchHistory(); showToast(`${company} researched successfully`); }
      setLoading(false);
    }, token);
  }

  function exportCSV() {
    if (!history.length) return;
    const headers = ['Company','Score','Tier','Budget','Timestamp'];
    const rows = history.map(h => [h.company, parseScore(h.score), parseTier(h.score), parseBudget(h.score), new Date(h.timestamp).toLocaleString()]);
    const csv = [headers,...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'rakaba-leads.csv'; a.click();
    showToast('CSV exported');
  }

  function clearHistory() {
    if (!window.confirm('Clear all lead history?')) return;
    setHistory([]); showToast('History cleared');
  }

  const progressPct = () => {
    const done = AGENT_STEPS.filter(s => stepStatus[s.id] === 'done').length;
    return Math.round((done / AGENT_STEPS.length) * 100);
  };

  const tier     = parseTier(report?.score);
  const tierConf = TIER_CONFIG[tier] || TIER_CONFIG.UNKNOWN;
  const score    = parseScore(report?.score);

  return (
    <div className="lrp-root">

      {/* Toast */}
      {toast && (
        <div className={`lrp-toast ${toast.type === 'error' ? 'error' : ''}`}>
          {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.msg}
        </div>
      )}

      {/* ── RAKABA NAV ── */}
      <nav className="lrp-nav">
        <a href="/leads" className="lrp-nav-logo">
          <RakabaLogo size={26}/>
          <span className="lrp-nav-brand">Raka<span>ba</span></span>
        </a>
        <div className="lrp-nav-divider"/>
        <span className="lrp-nav-product">Lead Research</span>
        <div className="lrp-nav-spacer"/>
      <div className="lrp-nav-actions" style={{display:"flex",alignItems:"center",gap:"10px"}}>
        {usage && (
          <div className="lrp-usage-pill">
            <span className="lrp-usage-text">{usage.leads.used}/{usage.leads.limit === -1 ? '∞' : usage.leads.limit} leads</span>
            <div className="lrp-usage-bar">
              <div className="lrp-usage-fill" style={{width: usage.leads.limit === -1 ? '10%' : `${Math.min(100,(usage.leads.used/usage.leads.limit)*100)}%`}}/>
            </div>
          </div>
        )}
        {usage && usage.plan === 'free' && (
          <button className="lrp-upgrade-btn" onClick={() => setShowUpgrade(true)}>
            ⚡ Upgrade
          </button>
        )}
        {usage && usage.plan !== 'free' && (
          <span className="lrp-plan-badge">{usage.plan.toUpperCase()}</span>
        )}
        <div className="lrp-user-menu" onClick={() => setShowProfile(p => !p)}>
          {user?.imageUrl
            ? <img src={user.imageUrl} alt="avatar" className="lrp-avatar"/>
            : <div className="lrp-avatar-placeholder">{user?.firstName?.[0] || 'U'}</div>
          }
          <div className="lrp-user-dropdown">
            <div className="lrp-user-info">
              <div className="lrp-user-name">{user?.fullName || 'User'}</div>
              <div className="lrp-user-email">{user?.primaryEmailAddress?.emailAddress}</div>
            </div>
            <div className="lrp-dropdown-divider"/>
            {usage?.plan !== 'free' && (
              <button className="lrp-dropdown-item" onClick={handleBillingPortal}>
                💳 Manage billing
              </button>
            )}
            <button className="lrp-dropdown-item" onClick={() => setShowUpgrade(true)}>
              ⚡ Upgrade plan
            </button>
            <div className="lrp-dropdown-divider"/>
            <button className="lrp-dropdown-item danger" onClick={() => signOut(() => window.location.href='/sign-in')}>
              Sign out
            </button>
          </div>
        </div>
      </div>
      </nav>

      {/* ── HERO ── */}
      <div className="lrp-hero">
        <div className="lrp-hero-inner">
          <div className="lrp-hero-badge">
            <span className="lrp-hero-dot"/>
            AI Sales Intelligence
          </div>
          <h1 className="lrp-hero-title">
            Lead Research <span className="lrp-hero-accent">Pipeline</span>
          </h1>

          {/* Search */}
          <div className="lrp-search-wrap">
            <div className="lrp-search-box">
              <span className="lrp-search-icon">🏢</span>
              <input
                ref={inputRef}
                type="text"
                placeholder="Enter company name — e.g. Stripe, Databricks, Notion..."
                value={company}
                onChange={e => setCompany(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResearch()}
                disabled={loading}
                className="lrp-search-input"
              />
              <button onClick={handleResearch} disabled={loading || !company.trim()} className="lrp-search-btn">
                {loading ? <span className="lrp-btn-spinner"/> : '→ Research'}
              </button>
            </div>

            {/* Progress */}
            {loading && (
              <div className="lrp-progress-wrap">
                <div className="lrp-agent-steps">
                  {AGENT_STEPS.map((step, i) => {
                    const st = stepStatus[step.id];
                    return (
                      <div key={step.id} className={`lrp-agent-step ${st || 'idle'}`}>
                        <div className="lrp-step-icon">
                          {st === 'done' ? '✓' : st === 'running' ? <span className="lrp-mini-spinner"/> : step.icon}
                        </div>
                        <div className="lrp-step-info">
                          <div className="lrp-step-label">{step.label}</div>
                          {st === 'running' && <div className="lrp-step-desc">{step.desc}</div>}
                        </div>
                        {i < AGENT_STEPS.length - 1 && <div className={`lrp-step-arrow ${st === 'done' ? 'done' : ''}`}>›</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="lrp-bar-track"><div className="lrp-bar-fill" style={{width:`${progressPct()}%`}}/></div>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="lrp-stats">
            <div className="lrp-stat"><span className="lrp-stat-num">{history.length}</span><span className="lrp-stat-label">Leads</span></div>
            <div className="lrp-stat-div"/>
            <div className="lrp-stat"><span className="lrp-stat-num" style={{color:'#ef4444'}}>{history.filter(h=>parseTier(h.score)==='HOT').length}</span><span className="lrp-stat-label">Hot</span></div>
            <div className="lrp-stat-div"/>
            <div className="lrp-stat"><span className="lrp-stat-num" style={{color:'#f59e0b'}}>{history.filter(h=>parseTier(h.score)==='WARM').length}</span><span className="lrp-stat-label">Warm</span></div>
            <div className="lrp-stat-div"/>
            <div className="lrp-stat"><span className="lrp-stat-num">4</span><span className="lrp-stat-label">AI Agents</span></div>
            <div className="lrp-stat-div"/>
            <div className="lrp-stat"><span className="lrp-stat-num" style={{fontSize:14}}>Auto</span><span className="lrp-stat-label">Sheets Sync</span></div>
          </div>
        </div>
      </div>

      {/* ── PAGE TABS ── */}
      <div className="lrp-tabs-nav">
        {[{id:'research',label:'🔍 Research'},{id:'dashboard',label:'📊 Dashboard'}].map(t => (
          <button key={t.id} onClick={() => setPageTab(t.id)} className={`lrp-tab-nav-btn ${pageTab===t.id?'active':''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── DASHBOARD ── */}
      {pageTab === 'dashboard' && (
        <div className="lrp-dashboard-wrap">
          <Dashboard leads={history} onSelectLead={async lead => { const full = await fetchFullLead(lead.id); if (full) setReport(full); setActiveTab('research'); setPageTab('research'); }}/>
        </div>
      )}

      {/* ── RESEARCH ── */}
      {pageTab === 'research' && (
        <div className="lrp-body">

          {/* LEFT */}
          <div className="lrp-report-col">
            {!report && !loading && (
              <div className="lrp-empty">
                <div className="lrp-empty-icon">🎯</div>
                <h3>Research a company</h3>
                <p>Enter a company name above to run the full 4-agent pipeline</p>
                <div className="lrp-empty-agents">
                  {AGENT_STEPS.map(s => (
                    <div key={s.id} className="lrp-empty-agent">
                      <span>{s.icon}</span><span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report && (
              <div className="lrp-report">
                {/* Score Hero */}
                <div className="lrp-score-hero" style={{background:tierConf.bg, boxShadow:tierConf.glow, borderColor:tierConf.color+'30'}}>
                  <div className="lrp-score-left">
                    {favicon && <img src={favicon} alt="" className="lrp-company-favicon" onError={e=>e.target.style.display='none'}/>}
                    <div>
                      <h2 className="lrp-company-name">{report.company}</h2>
                      <div className="lrp-tier-pill" style={{background:tierConf.color}}>{tierConf.label}</div>
                      <div className="lrp-report-meta">{new Date(report.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="lrp-score-ring">
                    <svg viewBox="0 0 100 100" className="lrp-score-svg">
                      <circle cx="50" cy="50" r="42" className="lrp-ring-bg"/>
                      <circle cx="50" cy="50" r="42" className="lrp-ring-fill" style={{stroke:tierConf.color, strokeDashoffset:264-(264*score/10)}}/>
                    </svg>
                    <div className="lrp-score-text">
                      <span className="lrp-score-num">{score}</span>
                      <span className="lrp-score-denom">/10</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="lrp-quick-stats">
                  <div className="lrp-qs-item"><span className="lrp-qs-label">Budget</span><span className="lrp-qs-value">{parseBudget(report.score)}</span></div>
                  <div className="lrp-qs-item"><span className="lrp-qs-label">Timing</span><span className="lrp-qs-value">{parseTiming(report.score)}</span></div>
                  <div className="lrp-qs-item"><span className="lrp-qs-label">Opportunity</span><span className="lrp-qs-value lrp-qs-opp">{parseOpportunity(report.score)}</span></div>
                </div>

                {/* Pain Points */}
                {parsePainPoints(report.score).length > 0 && (
                  <div className="lrp-pain-points">
                    <div className="lrp-pp-label">Pain Points</div>
                    {parsePainPoints(report.score).map((pp, i) => (
                      <div key={i} className="lrp-pp-item">
                        <span style={{color:tierConf.color, fontSize:10}}>▸</span> {pp}
                      </div>
                    ))}
                  </div>
                )}

                {/* Report Tabs */}
                <div className="lrp-tabs">
                  {['research','score','email'].map(tab => (
                    <button key={tab} className={`lrp-tab ${activeTab===tab?'active':''}`}
                      onClick={() => setActiveTab(tab)}
                      style={activeTab===tab ? {color:tierConf.color, borderBottomColor:tierConf.color} : {}}>
                      {tab==='research'?'🔍 Research':tab==='score'?'📊 Score':'📧 Email'}
                    </button>
                  ))}
                </div>

                <div className="lrp-tab-content">
                  {activeTab === 'research' && (
                    <div className="lrp-research-content">
                      <div className="lrp-section"><div className="lrp-section-title">Company Summary</div><p>{report.research}</p></div>
                      <div className="lrp-section"><div className="lrp-section-title">Latest News</div><p>{report.news}</p></div>
                      <div className="lrp-two-col">
                        <div className="lrp-section"><div className="lrp-section-title">Tech Stack</div><p>{report.tech}</p></div>
                        <div className="lrp-section"><div className="lrp-section-title">Funding</div><p>{report.funding}</p></div>
                      </div>
                      {report.sources && report.sources.length > 0 && (
                        <div className="lrp-sources">
                          <div className="lrp-sources-title">
                            <span>🔗</span> Sources
                            <span className="lrp-sources-disclaimer">Verify key figures before outreach</span>
                          </div>
                          <div className="lrp-sources-list">
                            {report.sources.map((s, i) => (
                              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="lrp-source-item">
                                <span className={`lrp-source-type lrp-source-${s.type}`}>
                                  {s.type === 'news' ? '📰' : s.type === 'official' ? '🏢' : '🌐'}
                                </span>
                                <span className="lrp-source-title">{s.title}</span>
                                <span className="lrp-source-domain">{new URL(s.url).hostname.replace('www.','')}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {activeTab === 'score' && <pre className="lrp-score-pre">{report.score}</pre>}
                  {activeTab === 'email' && (
                    <div>
                      {/* Variant selector */}
                      {report.emailVariants && report.emailVariants.length > 1 && (
                        <div className="lrp-email-variants">
                          {[
                            { label: '🎯 Problem-led', idx: 0 },
                            { label: '💡 Insight-led', idx: 1 },
                            { label: '📈 Results-led', idx: 2 }
                          ].map(({ label, idx }) => (
                            <button
                              key={idx}
                              className={`lrp-variant-btn ${activeEmailVariant === idx ? 'active' : ''}`}
                              onClick={() => setActiveEmailVariant(idx)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="lrp-email-box">
                        <pre>{report.emailVariants ? report.emailVariants[activeEmailVariant] : report.email}</pre>
                      </div>
                      <div className="lrp-email-actions">
                        <button className="lrp-action-btn secondary" onClick={() => {
                          const txt = report.emailVariants ? report.emailVariants[activeEmailVariant] : report.email;
                          navigator.clipboard.writeText(txt);
                          showToast('Email copied');
                        }}>📋 Copy</button>
                        <button className="lrp-action-btn primary" onClick={async () => {
                          const txt = report.emailVariants ? report.emailVariants[activeEmailVariant] : report.email;
                          const subjMatch = txt.match(/SUBJECT:\s*(.+)/);
                          const subject = subjMatch ? subjMatch[1].trim() : `Re: ${report.company}`;
                          const body = txt.replace(/SUBJECT:.+\n/,'').trim();
                          const to = prompt('Send to email address:');
                          if (!to) return;
                          try {
                            const r = await fetch(`${BACKEND_URL}/api/leads/send-email`, { method:'POST', headers:{'Content-Type':'application/json','X-API-Key':process.env.REACT_APP_API_KEY}, body:JSON.stringify({to,subject,body,company:report.company}) });
                            const d = await r.json();
                            if (d.success) showToast('Email sent'); else showToast(d.error,'error');
                          } catch(err) { showToast(err.message,'error'); }
                        }}>📧 Send</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lrp-sidebar">
            <div className="lrp-sidebar-header">
              <h3>Recent Leads</h3>
              <div className="lrp-sidebar-actions">
                {history.length > 0 && <>
                  <button className="lrp-icon-btn" onClick={exportCSV} title="Export CSV">↓</button>
                  <button className="lrp-icon-btn danger" onClick={clearHistory} title="Clear">×</button>
                </>}
              </div>
            </div>

            {history.length === 0 ? (
              fetchError
                ? <div className="lrp-empty-error">
                    <div className="lrp-empty-error-icon">⚠️</div>
                    <div className="lrp-empty-error-title">Couldn't load history</div>
                    <div className="lrp-empty-error-body">{fetchError}</div>
                    <button className="lrp-error-retry" onClick={fetchHistory}>Try again</button>
                  </div>
                : <div className="lrp-sidebar-empty">No leads yet — research a company to get started</div>
            ) : (
              <div className="lrp-history-list">
                {history.map((h, i) => {
                  const t = parseTier(h.score);
                  const tc = TIER_CONFIG[t] || TIER_CONFIG.UNKNOWN;
                  return (
                    <div key={i} className="lrp-history-item-wrap">
                      <div className="lrp-history-item" onClick={async () => { const full = await fetchFullLead(h.id); if (full) setReport(full); setActiveTab('research'); }} style={{borderLeftColor:tc.color}}>
                        <div className="lrp-hi-top">
                          <span className="lrp-hi-company">{h.company}</span>
                          <span className="lrp-hi-score" style={{color:tc.color}}>{parseScore(h.score)}/10</span>
                        </div>
                        <div className="lrp-hi-bottom">
                          <span className="lrp-hi-tier" style={{background:tc.bg,color:tc.color}}>{tc.label}</span>
                          <span className="lrp-hi-date">{new Date(h.timestamp).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="lrp-note-wrap" onClick={e => e.stopPropagation()}>
                        <textarea
                          className="lrp-note-input"
                          placeholder="Add a note..."
                          value={notes[h.id] || ''}
                          onChange={e => setNotes(prev => ({...prev, [h.id]: e.target.value}))}
                          onBlur={e => saveNote(h.id, e.target.value)}
                          rows={2}
                        />
                        {savingNote === h.id && <span className="lrp-note-saving">saving...</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── ONBOARDING ── */}
      {showOnboarding && (
        <div className="lrp-modal-overlay">
          <div className="lrp-onboard-modal">
            {/* Progress dots */}
            <div className="lrp-onboard-dots">
              {[0,1,2].map(i => (
                <div key={i} className={`lrp-onboard-dot ${onboardStep === i ? 'active' : onboardStep > i ? 'done' : ''}`}/>
              ))}
            </div>

            {onboardStep === 0 && (
              <div className="lrp-onboard-step">
                <div className="lrp-onboard-icon">👋</div>
                <h2 className="lrp-onboard-title">Welcome to Rakaba</h2>
                <p className="lrp-onboard-body">
                  Rakaba replaces hours of manual sales research with a single company name.
                  In 60 seconds you'll have a full brief, a lead score, and a personalized cold email.
                </p>
                <div className="lrp-onboard-feature-list">
                  <div className="lrp-onboard-feature"><span>🔍</span><span>Deep company research from 4 parallel sources</span></div>
                  <div className="lrp-onboard-feature"><span>📊</span><span>BANT lead scoring — HOT, WARM, or COLD</span></div>
                  <div className="lrp-onboard-feature"><span>✉️</span><span>Personalized cold email written from the research</span></div>
                </div>
                <button className="lrp-onboard-btn" onClick={onboardNext}>Get started →</button>
              </div>
            )}

            {onboardStep === 1 && (
              <div className="lrp-onboard-step">
                <div className="lrp-onboard-icon">🏢</div>
                <h2 className="lrp-onboard-title">Pick your first company</h2>
                <p className="lrp-onboard-body">
                  Type any company you're considering reaching out to. Try a company you've been meaning to prospect.
                </p>
                <div className="lrp-onboard-examples">
                  <div className="lrp-onboard-example-title">Popular first searches:</div>
                  {['Stripe', 'Notion', 'HubSpot', 'Figma', 'Vercel'].map(c => (
                    <button key={c} className="lrp-onboard-company-btn" onClick={() => {
                      completeOnboarding();
                      setTimeout(() => {
                        const input = document.querySelector('.lrp-search-input');
                        if (input) { input.value = c; input.focus(); }
                      }, 100);
                    }}>{c}</button>
                  ))}
                </div>
                <div className="lrp-onboard-actions">
                  <button className="lrp-onboard-btn-secondary" onClick={() => setOnboardStep(0)}>← Back</button>
                  <button className="lrp-onboard-btn" onClick={onboardNext}>I'll type my own →</button>
                </div>
              </div>
            )}

            {onboardStep === 2 && (
              <div className="lrp-onboard-step">
                <div className="lrp-onboard-icon">🚀</div>
                <h2 className="lrp-onboard-title">You're ready</h2>
                <p className="lrp-onboard-body">
                  You have <strong>5 free lead researches</strong> this month. Each one gives you a full company brief, lead score, and cold email.
                </p>
                <div className="lrp-onboard-tip">
                  <span className="lrp-onboard-tip-icon">💡</span>
                  <span>Pro tip — start with companies you already know are a good fit. The scoring works best when you can validate it against your intuition.</span>
                </div>
                <div className="lrp-onboard-actions">
                  <button className="lrp-onboard-btn-secondary" onClick={() => setOnboardStep(1)}>← Back</button>
                  <button className="lrp-onboard-btn" onClick={completeOnboarding}>Start researching →</button>
                </div>
              </div>
            )}

            <button className="lrp-onboard-skip" onClick={completeOnboarding}>Skip intro</button>
          </div>
        </div>
      )}


      {/* ── SENDER PROFILE SETUP ── */}
      {showSenderSetup && (
        <div className="lrp-modal-overlay" onClick={() => setShowSenderSetup(false)}>
          <div className="lrp-modal" style={{maxWidth:'520px'}} onClick={e => e.stopPropagation()}>
            <div className="lrp-modal-header">
              <h2 className="lrp-modal-title">✉️ Sender profile</h2>
              <button className="lrp-modal-close" onClick={() => setShowSenderSetup(false)}>✕</button>
            </div>
            <p className="lrp-modal-sub">Tell Rakaba what you sell — every cold email will be written from your perspective, not a generic vendor.</p>

            <div className="lrp-form">
              <div className="lrp-form-group">
                <label className="lrp-form-label">Your role / title</label>
                <input className="lrp-form-input" placeholder="e.g. Account Executive at Acme Corp"
                  value={senderForm.sender_role}
                  onChange={e => setSenderForm(f => ({...f, sender_role: e.target.value}))}/>
              </div>
              <div className="lrp-form-group">
                <label className="lrp-form-label">What you sell <span className="lrp-form-required">*</span></label>
                <input className="lrp-form-input" placeholder="e.g. DevOps automation platform for engineering teams"
                  value={senderForm.sender_product}
                  onChange={e => setSenderForm(f => ({...f, sender_product: e.target.value}))}/>
              </div>
              <div className="lrp-form-group">
                <label className="lrp-form-label">Your ideal customer</label>
                <input className="lrp-form-input" placeholder="e.g. Series B+ SaaS companies with 50-500 engineers"
                  value={senderForm.sender_icp}
                  onChange={e => setSenderForm(f => ({...f, sender_icp: e.target.value}))}/>
              </div>
              <div className="lrp-form-group">
                <label className="lrp-form-label">Your value proposition</label>
                <textarea className="lrp-form-textarea" placeholder="e.g. We cut deployment time by 60% and reduce infra costs by 30% for mid-size engineering teams"
                  value={senderForm.sender_value_prop}
                  onChange={e => setSenderForm(f => ({...f, sender_value_prop: e.target.value}))}/>
              </div>
              <div className="lrp-form-group">
                <label className="lrp-form-label">Email tone</label>
                <div className="lrp-tone-options">
                  {['direct','consultative','casual','formal'].map(t => (
                    <button key={t} className={`lrp-tone-btn ${senderForm.sender_tone === t ? 'active' : ''}`}
                      onClick={() => setSenderForm(f => ({...f, sender_tone: t}))}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'1rem'}}>
                <button className="lrp-price-btn outlined" onClick={() => setShowSenderSetup(false)}>Cancel</button>
                <button className="lrp-price-btn solid" onClick={saveSenderProfile}
                  disabled={!senderForm.sender_product}>
                  Save profile →
                </button>
              </div>
            </div>

            {senderProfile?.sender_product && (
              <div className="lrp-sender-preview">
                <div className="lrp-sender-preview-label">Current profile</div>
                <div className="lrp-sender-preview-text">"{senderProfile.sender_product}"</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PROFILE PANEL ── */}
      {showProfile && (
        <div className="lrp-modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="lrp-profile-panel" onClick={e => e.stopPropagation()}>
            <div className="lrp-profile-header">
              {user?.imageUrl
                ? <img src={user.imageUrl} alt="avatar" className="lrp-profile-avatar"/>
                : <div className="lrp-profile-avatar-lg">{user?.firstName?.[0] || 'U'}</div>
              }
              <div>
                <div className="lrp-profile-name">{user?.fullName || 'User'}</div>
                <div className="lrp-profile-email">{user?.primaryEmailAddress?.emailAddress}</div>
                <span className="lrp-profile-plan-badge">{usage?.plan?.toUpperCase() || 'FREE'}</span>
              </div>
            </div>

            <div className="lrp-profile-section">
              <div className="lrp-profile-section-title">Usage this month</div>
              <div className="lrp-profile-usage-row">
                <span>Lead researches</span>
                <span className="lrp-profile-usage-val">
                  {usage?.leads?.used || 0} / {usage?.leads?.limit === -1 ? '∞' : (usage?.leads?.limit || 5)}
                </span>
              </div>
              <div className="lrp-profile-bar-wrap">
                <div className="lrp-profile-bar-fill" style={{
                  width: usage?.leads?.limit === -1 ? '5%' :
                    `${Math.min(100, ((usage?.leads?.used || 0) / (usage?.leads?.limit || 5)) * 100)}%`
                }}/>
              </div>
              {usage?.plan === 'free' && (
                <button className="lrp-profile-upgrade-cta" onClick={() => { setShowProfile(false); setShowUpgrade(true); }}>
                  ⚡ Upgrade for more leads
                </button>
              )}
            </div>

            <div className="lrp-profile-section">
              <div className="lrp-profile-section-title">Account</div>
              <div className="lrp-profile-actions">
              <button className="lrp-profile-action-btn highlight" onClick={() => { setShowProfile(false); setShowSenderSetup(true); }}>
                ✉️ {senderProfile?.sender_product ? 'Edit sender profile' : 'Set up sender profile'}
              </button>
                {usage?.plan !== 'free' && (
                  <button className="lrp-profile-action-btn" onClick={() => { setShowProfile(false); handleBillingPortal(); }}>
                    💳 Manage billing
                  </button>
                )}
                <button className="lrp-profile-action-btn" onClick={() => { setShowProfile(false); setShowUpgrade(true); }}>
                  ⚡ Upgrade plan
                </button>
                <button className="lrp-profile-action-btn danger" onClick={() => signOut(() => window.location.href='/sign-in')}>
                  → Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── UPGRADE MODAL ── */}
      {showUpgrade && (
        <div className="lrp-modal-overlay" onClick={() => setShowUpgrade(false)}>
          <div className="lrp-modal" onClick={e => e.stopPropagation()}>
            <div className="lrp-modal-header">
              <h2 className="lrp-modal-title">Upgrade Rakaba</h2>
              <button className="lrp-modal-close" onClick={() => setShowUpgrade(false)}>✕</button>
            </div>
            <p className="lrp-modal-sub">Unlock more leads, bulk research, and team features.</p>
            <div className="lrp-pricing-grid">
              <div className="lrp-price-card">
                <div className="lrp-price-plan">Pro</div>
                <div className="lrp-price-amount">$29<span>/mo</span></div>
                <ul className="lrp-price-features">
                  <li>100 lead researches/month</li>
                  <li>Lead history & notes</li>
                  <li>3 email angle variants</li>
                  <li>Google Sheets sync</li>
                </ul>
                <button className="lrp-price-btn solid" onClick={() => handleUpgrade('pro')}>
                  Upgrade to Pro →
                </button>
              </div>
              <div className="lrp-price-card featured">
                <div className="lrp-price-badge">Most popular</div>
                <div className="lrp-price-plan">Team</div>
                <div className="lrp-price-amount">$79<span>/mo</span></div>
                <ul className="lrp-price-features">
                  <li>500 lead researches/month</li>
                  <li>Up to 5 seats</li>
                  <li>HubSpot integration</li>
                  <li>Shared lead lists</li>
                </ul>
                <button className="lrp-price-btn solid" onClick={() => handleUpgrade('team')}>
                  Upgrade to Team →
                </button>
              </div>
              <div className="lrp-price-card">
                <div className="lrp-price-plan">Agency</div>
                <div className="lrp-price-amount">$199<span>/mo</span></div>
                <ul className="lrp-price-features">
                  <li>Unlimited lead researches</li>
                  <li>Unlimited seats</li>
                  <li>Full API access</li>
                  <li>White-label exports</li>
                </ul>
                <button className="lrp-price-btn outlined" onClick={() => handleUpgrade('agency')}>
                  Upgrade to Agency →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}